# Events

## What’s an event?

Events are emitted when certain actions occur on SEEK.
For example, an event may be emitted when:

- A posted job ad has gone live on the SEEK job board

- A candidate has applied for a job ad

A complete list of event types is available in the [event types] section of this page and in our [webhook schema].

You can use events to trigger workflows in your software.
SEEK can deliver events in two ways:

- Pushed into your software via [webhooks](/events/webhooks)

- Pulled from your software via [polling](/events/polling) the GraphQL `events` query

The SEEK API does not use GraphQL subscriptions as these are not a good fit for machine-to-machine integrations.

You can view a list of your historical events on the [Developer Dashboard events page].
This lets you observe the events that are being emitted for all of your SEEK hirers.

[Developer Dashboard events page]: /manage/events
[Event types]: #event-types

## Event types

- [CandidateApplicationCreatedEvent]

  These events are emitted when a candidate submits an application for a position.

  Consuming these events will allow you to retrieve applications submitted using SEEK’s Apply Form; see [Optimised Apply] for more information.

import { Alert, List, Stack, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

<Alert tone="caution">
  <Stack space="medium">
    <Text weight="strong">
      It is essential that you consume these events for{' '}
      <SmartTextLink href="/use-cases/application-export">
        Optimised Apply
      </SmartTextLink>{' '}
      integrations. Failure to consume these events will mean:
    </Text>
    <List space="small">
      <Text>
        Hirers will be unable to view job applications via your platform. This
        wrongs hirers since they will not receive the full value of the
        advertisement they paid for.
      </Text>
      <Text>
        Candidates will not have their application delivered to the hirer. This
        wrongs candidates since they miss out on the value that SEEK offers
        them.
      </Text>
    </List>
  </Stack>
</Alert>

- [CandidateProfilePurchasedEvent]

  These events are emitted when a candidate profile is purchased in [Talent Search].

  It is essential to consume these events if you wish to import purchased candidate profiles into your recruitment software.

- [HirerRelationshipChangedEvent]

  These events are emitted whenever a relationship between your platform and a hirer is added or removed.

  The hirer relationships in your software should be kept in sync with SEEK using these events.

- [PositionProfilePostedEvent]

  These events are emitted when a job ad is posted.

  An advertisement’s status in your software should be kept in sync with SEEK using these events.

- [PositionProfileUpdatedEvent]

  These events are emitted when a job ad is updated.

  An advertisement’s status in your software should be kept in sync with SEEK using these events.

- [PositionProfileClosedEvent]

  These events are emitted when a job ad is closed.

  An advertisement’s status in your software should be kept in sync with SEEK using these events.

- [PositionNotPostedEvent]

  These events will be emitted when a position is not posted on SEEK.

  A position’s status in your software should be kept in sync with SEEK using these events.

[CandidateApplicationCreatedEvent]: /schema/webhooks/CandidateApplicationCreatedEvent
[Optimised Apply]: /use-cases/application-export
[CandidateProfilePurchasedEvent]: /schema/webhooks/CandidateProfilePurchasedEvent
[Talent Search]: https://au.employer.seek.com/products/talentsearch
[HirerRelationshipChangedEvent]: /schema/webhooks/HirerRelationshipChangedEvent
[PositionProfilePostedEvent]: /schema/webhooks/PositionProfilePostedEvent
[PositionProfileClosedEvent]: /schema/webhooks/PositionProfileClosedEvent
[PositionProfileUpdatedEvent]: /schema/webhooks/PositionProfileUpdatedEvent
[PositionNotPostedEvent]: /schema/webhooks/PositionNotPostedEvent

## Why webhooks?

Webhooks are preferred for a few key reasons:

- **Efficiency**

  With webhooks, you are only notified when an event actually occurs.
  You can place incoming events onto a queue, and leverage autoscaling and event-driven services to adjust to load.

  With polling, you need to query our GraphQL endpoint for new events on an interval.
  This leads to a tradeoff between latency and cost:
  polling infrequently means that your software will be slower to react to events,
  while polling too frequently wastes compute resources.

- **Scalability**

  With webhooks, you can distribute incoming events across multiple servers and process them in any order.

  With polling, you need to keep track of pagination cursors and retrieve pages in order.
  This makes it difficult to distribute your workload across multiple servers.

- **Simplicity**

  With webhooks, you can have a stateless service that simply processes the events that it receives.
  SEEK takes care of delivering the events to your endpoint;
  if your service is unreachable or returns an error,
  SEEK automatically re-queues the events for later.

  Webhooks also provide a mechanism for SEEK to resend historical events to you on request.
  This enables disaster recovery without adding any complexity to your integration.

  With polling, you need to manage state and the complexity of pagination.
  It can be easy to accidentally skip events while paging through results,
  and you will probably need to build the capability to retrieve and reprocess historical events for disaster recovery.

[webhook schema]: /schema/webhooks
